import { Alert, Box, Collapse, Typography } from "@mui/material";
import { Download } from "@mui/icons-material";
import { useEffect, useState } from "react";
import CustomTabs from "../../common/custom-tabs";
import ButtonOne from "../../common/normal-button";
import ButtonThree from "../../common/third-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_CLIENT_CSV_UPLOAD_ENDPOINT, MFD_DAILY_HOLDING_CSV_UPLOAD_ENDPOINT } from "../../config/urls";

const SOURCE_TABS = [
  { value: "redvision", label: "RedVision" },
  { value: "iwell",     label: "iWell" },
  { value: "bonanza",     label: "Bonanza" },
];

const UPLOAD_TYPE_TABS = [
  { value: "clients",        label: "Clients",        sources: ["redvision", "iwell"] },
  { value: "daily_holdings", label: "Daily Holdings", sources: ["redvision", "iwell", "bonanza"] },
];

const SOURCE_API_VALUE = {
  redvision: "RedVision",
  iwell:     "iWell",
  bonanza:   "Bonanza",
};

const SAMPLE_CLIENTS_CSV = [
  "Client Name,ID,PAN,Mobile,Email,Broker Name,Broker Code,Address 1,Address 2,Address 3,DOB",
  "John Doe,LC001,ABCDE1234F,9876543210,john@example.com,Wealth Partners,BRK001,Flat 101,MG Road,Bangalore,15/05/1985",
  "Jane Smith,LC002,FGHIJ5678K,9823456789,jane@example.com,FinGrowth,BRK002,Plot 45,Banjara Hills,Hyderabad,22/11/1990",
].join("\n");

const SAMPLE_HOLDINGS_REDVISION_CSV = [
  "PAN,Branch Name,Advisor Name,Investment,Div.Paid,Div.Reinv,AUM,Avg. Days,Today's Profit Loss,Gain/Loss,Abs.Return,XIRR(%)",
  "ABCDE1234F,Main Branch,Advisor A,100000,500,0,110000,365,100,10000,10,12.5",
  "FGHIJ5678K,North Branch,Advisor B,200000,0,1000,215000,180,-50,15000,7.5,15.0",
].join("\n");

const SAMPLE_HOLDINGS_IWELL_CSV = [
  "PAN,BALANCE UNITS,PURCHASE VALUE,DIVIDEND,CURRENT VALUE,AVG HOLDING DAYS,ONE DAY CHANGE,GAIN,ABSOLUTE RETURN,CAGR",
  "ABCDE1234F,100.5,100000,500,110000,365,100,10000,10,12.5",
  "FGHIJ5678K,250.0,200000,0,215000,180,-50,15000,7.5,15.0",
].join("\n");

const SAMPLE_HOLDINGS_BONANZA_CSV = [
  "TITLE,NAME,PAN,APPCODE,EMAIL,Disable Emails,SECONDARY EMAIL,IWELL CODE,USERNAME,MOBILE,LANDLINE,DATE OF BIRTH,BIRTHDAY WISH,ANNIVERSARY,DATE OF DEATH,FAMILY HEAD,FAMILY HEAD IWELL CODE,Referred By,IWELL CODE 2,FAMILY HEAD IWELL CODE 2,ADDRESS1,ADDRESS2,ADDRESS3,CITY,STATE,Country,PIN,CLIENT RATING,First Investment Date,REVIEW FREQUENCY,LAST REVIEW DATE,MODEL NAME,FILE NO,TAGS,UPDATE LOCK,EQUITY CODE1,EQUITY CODE2,DEPOSITORY,DP NAME,DP ID,NPS A/c No,ANNUAL INCOME,PROFESSION,Bill State,Bill GSTIN,AUM,Target SIP Amout,Target ELSS Amount,Target Equity Allocation (%),Target Debt Allocation (%),Preferred Billing mode,Equity MF Billing (%),Debt MF Billing (%),Shares Billing (%),Bonds Billing (%),Fixed Deposit Billing (%),Other Assets Billing (%),Remarks,BANK DETAILS,OS ADDRESS1,OS ADDRESS2,OS ADDRESS3,OVERSEAS CITY,OVERSEAS STATE,OVERSEAS COUNTRY,Overseas PIN,OVERSEAS PHONE,OVERSEAS MOBILE,TAX STATUS,MODE OF HOLDING,OCCUPATION,Joint Holder 1,Joint Holder 2,GUARDIAN NAME,GUARDIAN PAN,ATTORNEY NAME,ATTORNEY PAN,NOMINEE DETAILS,GROSS ANNUAL INCOME,NET WORTH,NET WORTH DATE,FOR INDIVIDUAL,FOR NON INDIVIDUAL,NATIONALITY,GENDER,PLACE OF BIRTH,COUNTRY OF BIRTH,FATHER NAME,MOTHER NAME,SPOUSE,KYC CHECK,Supplementary KYC,KYC Source,FATCA CAMS,FATCA KARVY,CKYC,FATCA OTHER DETAILS,SOURCE,CREATED AT,Invite Sent Date,Last Login Date,LEVEL NAME TO BE UPDATED,CODE TO BE UPDATED,LIQUILOAN NO,VIRTUAL  R M,VIRTUAL  R M CODE,FAMILY HEAD USERNAME",
  ",JOHN DOE,ABCDE1234F,,john@example.com,N,,360339013,JOHNDOE,9876543210,,15-05-1985,,,,,,,abc123,,FLAT 101 A WING SAMPLE APARTMENT,MG ROAD,BANGALORE,BANGALORE,KARNATAKA,,560001,,12-08-2025,,,,,,N,,,,,,,,,,,1500000,,,,,,0,0,0,0,0,0,,,,,,,,,,,,INDIVIDUAL,,,,,,,,,,,,,,,,,,,,,,,,,,,,,IIN Creation,1/1/2025,01-01-2025,01-06-2025,,,,,,",
  ",JANE SMITH,FGHIJ5678K,,jane@example.com,N,,360339014,JANESMITH,9823456789,,22-11-1990,,,,,,,def456,,PLOT 45 B WING EXAMPLE TOWERS,BANJARA HILLS,HYDERABAD,HYDERABAD,TELANGANA,,500034,,15-09-2024,,,,,,N,,,,,,,,,,,2800000,,,,,,0,0,0,0,0,0,,,,,,,,,,,,INDIVIDUAL,,,,,,,,,,,,,,,,,,,,,,,,,,,,,IIN Creation,2/2/2025,02-02-2025,02-06-2025,,,,,,",
].join("\n");

const downloadSampleCSV = (type, source) => {
  let content = "";
  let filename = "";

  if (type === "clients") {
    content = SAMPLE_CLIENTS_CSV;
    filename = "sample_clients.csv";
  } else {
    if (source === "iwell")        content = SAMPLE_HOLDINGS_IWELL_CSV;
    else if (source === "bonanza") content = SAMPLE_HOLDINGS_BONANZA_CSV;
    else                           content = SAMPLE_HOLDINGS_REDVISION_CSV;
    filename = `sample_holdings_${source}.csv`;
  }

  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const FileUpload = ({ pageParams }) => {
  const { getValidToken } = useAuth();

  const [source, setSource]         = useState("redvision");
  const [uploadType, setUploadType] = useState(pageParams?.type === "daily_holdings" ? "daily_holdings" : "clients");
  const [file, setFile]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [alertOpen, setAlertOpen]   = useState(false);
  const [alertStatus, setAlertStatus]   = useState("success");
  const [alertMessage, setAlertMessage] = useState("");

  useEffect(() => {
    if (pageParams?.type) {
      setUploadType(pageParams.type);
    }
  }, [pageParams]);

  useEffect(() => {
    if (!alertOpen) return;
    const id = setTimeout(() => setAlertOpen(false), 8000);
    return () => clearTimeout(id);
  }, [alertOpen]);

  const showAlert = (severity, message) => {
    setAlertStatus(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] ?? null);
    setResult(null);
    setAlertOpen(false);
  };

  const resetFileInput = () => {
    setFile(null);
    const el = document.getElementById("mfd-file-input");
    if (el) el.value = "";
  };

  const handleUpload = async () => {
    if (!file) {
      showAlert("error", "Please select a file to upload.");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token  = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formData = new FormData();
      formData.append("file",   file);
      formData.append("source", SOURCE_API_VALUE[source]);

      const endpoint = uploadType === "clients" 
        ? MFD_CLIENT_CSV_UPLOAD_ENDPOINT 
        : MFD_DAILY_HOLDING_CSV_UPLOAD_ENDPOINT;

      const res  = await fetch(endpoint, {
        method:  "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        showAlert("error", data.error || "Upload failed.");
        return;
      }

      setResult(data);
      const hasErrors  = data.errors?.length > 0;
      const hasSuccess = data.inserted > 0 || data.updated > 0;

      if (!hasErrors) {
        showAlert("success", `Upload complete: ${data.inserted} inserted, ${data.updated} updated.`);
      } else if (hasSuccess) {
        showAlert(
          "warning",
          `Partial success: ${data.inserted} inserted, ${data.updated} updated. ${data.failed} row(s) failed.`,
        );
      } else {
        showAlert("error", `Upload failed — ${data.failed} row(s) had errors.`);
      }

      resetFileInput();
    } catch (err) {
      console.error("Upload error:", err);
      showAlert("error", "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>

      {/* Source */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ ...typography.h2, mb: 1 }}>Source</Typography>
        <CustomTabs
          value={source}
          onChange={(_, val) => {
            setSource(val);
            setResult(null);
            const allowed = UPLOAD_TYPE_TABS.filter((t) => t.sources.includes(val));
            if (!allowed.find((t) => t.value === uploadType)) {
              setUploadType(allowed[0].value);
            }
          }}
          tabs={SOURCE_TABS}
          sx={{ maxWidth: 320 }}
        />
      </Box>

      {/* Upload type */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography sx={{ ...typography.h2, mb: 1 }}>Upload Type</Typography>
        <CustomTabs
          value={uploadType}
          onChange={(_, val) => {
            setUploadType(val);
            setResult(null);
          }}
          tabs={UPLOAD_TYPE_TABS.filter((t) => t.sources.includes(source))}
          sx={{ maxWidth: 380 }}
        />
      </Box>

      {/* Sample CSV download */}
      <Box sx={{ mb: 3, width: "100%" }}>
        <Box sx={{ mb: 1 }}>
          <ButtonThree
            startIcon={<Download />}
            onClick={() => downloadSampleCSV(uploadType, source)}
          >
            Download Sample CSV ({uploadType === "clients" ? "Clients" : `Holdings - ${SOURCE_API_VALUE[source]}`})
          </ButtonThree>
        </Box>
        
        {uploadType === "clients" ? (
          <Typography variant="caption" color="text.secondary" display="block">
            Required: Client Name, ID, PAN, Mobile (10-digit Indian number) &nbsp;·&nbsp; Optional: Email, Broker Name, Broker Code, Address 1-3, DOB (dd/mm/yyyy or dd/mm/yy)
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block">
            Required: PAN (must match an existing client for this source) &nbsp;·&nbsp; Other columns vary by source.
          </Typography>
        )}
      </Box>

      {/* File input */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ...typography.bodyLarge, mb: 1 }}>Select a file to upload:</Typography>
        <input
          id="mfd-file-input"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
        />
      </Box>

      {/* Alert — sits just above the upload button */}
      <Collapse in={alertOpen} sx={{ mb: alertOpen ? 1.5 : 0, width: "100%", maxWidth: 600 }}>
        <Alert severity={alertStatus} onClose={() => setAlertOpen(false)}>
          {alertMessage}
        </Alert>
      </Collapse>

      <ButtonOne
        title={loading ? "Uploading…" : "Upload"}
        variant="contained"
        onClick={handleUpload}
        disabled={loading || !file}
      />

      {/* Upload Summary */}
      {result && (
        <Box sx={{ mt: 3, width: "100%", maxWidth: 600 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Upload Summary
          </Typography>
          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {[
              { label: "Total Records",  value: result.total   ?? "—" },
              { label: "Inserted",       value: result.inserted ?? "—", color: "success.main" },
              { label: "Updated",        value: result.updated  ?? "—", color: "info.main" },
              { label: "Failed",         value: result.failed   ?? "—", color: result.failed > 0 ? "error.main" : "text.primary" },
              { label: "Skipped",        value: result.skipped  ?? "—" },
              ...(result.clients_inserted != null ? [
                { label: "Clients Inserted", value: result.clients_inserted, color: "success.main" },
                { label: "Clients Updated",  value: result.clients_updated,  color: "info.main" },
              ] : []),
            ].map(({ label, value, color }, i) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.75,
                  bgcolor: i % 2 === 0 ? "#F9FAFB" : "#FFFFFF",
                }}
              >
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600} color={color || "text.primary"}>
                  {typeof value === "number" ? value.toLocaleString() : value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Per-row errors */}
      {result?.errors?.length > 0 && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: 600 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Failure Details ({result.errors.length})
          </Typography>
          <Box
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid #E5E7EB",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            {result.errors.map(({ row, errors }) => (
              <Box
                key={row}
                sx={{
                  mb: 1, pb: 1,
                  borderBottom: "1px solid #F3F4F6",
                  "&:last-child": { mb: 0, pb: 0, borderBottom: "none" },
                }}
              >
                <Typography variant="caption" fontWeight={700} color="error.main">
                  Row {row}
                </Typography>
                {errors.map((msg, i) => (
                  <Typography key={i} variant="caption" display="block" color="text.secondary">
                    &bull; {msg}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default FileUpload;
